import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface IdCardLockSetting {
  organizationId: string;
  locked: boolean;
}

export interface OrgIdCardClassOption {
  id: string;
  name: string;
}

const BANNER_STYLE = `<style>
  .id-cards-banner {
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    background: #1d4ed8; color: #fff; padding: 16px 24px; font-family: Arial, Helvetica, sans-serif;
  }
  .id-cards-banner h1 { margin: 0 0 2px; font-size: 16px; }
  .id-cards-banner p { margin: 0; font-size: 12px; opacity: 0.9; }
  .id-cards-banner button {
    background: #16a34a; color: #fff; border: none; border-radius: 6px; padding: 10px 20px;
    font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  }
  .id-cards-banner button:hover { background: #15803d; }
  @media print { .id-cards-banner { display: none; } }
</style>`;

function printBannerHtml(title: string): string {
  return `<div class="id-cards-banner">
    <div><h1>${title}</h1><p>Review the cards below, then print.</p></div>
    <button onclick="window.print()">Print</button>
  </div>`;
}

/**
 * Opens a print-ready HTML page in a new tab — the browser's own print
 * dialog produces the physical PDF, same as the real legacy behavior
 * (confirmed by reading DataExportController.php directly: it returns a
 * Blade view, not a server-rendered PDF). Adds a results banner + explicit
 * "Print" button (matching legacy's "Student ID Cards Generated" screen)
 * rather than auto-triggering `window.print()` on load — that interrupted
 * before the page was even visible. Injected client-side rather than baked
 * into the shared `renderStudentCardsHtml`/`renderStaffCardsHtml` templates
 * — those are also used by the school-tier Verify ID Cards preview iframe,
 * which shouldn't show a bulk-print banner. `.id-cards-banner` is hidden via
 * `@media print` so it never appears in the actual printed/PDF output.
 */
function openHtmlForPrint(html: string, title: string) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups to view the generated ID cards.');
    return;
  }
  let out = html.includes('</head>') ? html.replace('</head>', `${BANNER_STYLE}</head>`) : BANNER_STYLE + html;
  out = out.includes('<body>') ? out.replace('<body>', `<body>${printBannerHtml(title)}`) : printBannerHtml(title) + out;
  w.document.open();
  w.document.write(out);
  w.document.close();
}

export const OrgIdCardsApi = {
  getLockSetting: async (organizationId?: string): Promise<IdCardLockSetting> =>
    unwrap(await api.get('/org/id-cards/lock-setting', { params: organizationId ? { organizationId } : undefined })),

  setLockSetting: async (locked: boolean, organizationId?: string): Promise<IdCardLockSetting> =>
    unwrap(await api.put('/org/id-cards/lock-setting', { locked, organizationId })),

  listClasses: async (schoolId: string): Promise<OrgIdCardClassOption[]> =>
    unwrap(await api.get(`/org/id-cards/schools/${schoolId}/classes`)),

  generateStudents: async (params: { schoolId?: string; classId?: string }): Promise<void> => {
    const res = await api.post('/org/id-cards/generate/students', params, { responseType: 'text' });
    openHtmlForPrint(res.data as unknown as string, 'Student ID Cards Generated');
  },

  generateTeachers: async (params: { schoolId?: string; joiningYear?: number }): Promise<void> => {
    const res = await api.post('/org/id-cards/generate/teachers', params, { responseType: 'text' });
    openHtmlForPrint(res.data as unknown as string, 'Teacher ID Cards Generated');
  },

  generateNonTeachingStaff: async (params: { schoolId?: string; joiningYear?: number }): Promise<void> => {
    const res = await api.post('/org/id-cards/generate/non-teaching-staff', params, { responseType: 'text' });
    openHtmlForPrint(res.data as unknown as string, 'Non-Teaching Staff ID Cards Generated');
  },
};
